import { useEffect, useRef, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { OrderLineTypes, RegisterFunctions } from "@repo/database";
import { generateRandomString } from "@repo/utils";
import { useNavigate } from "@tanstack/react-router";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { Receipt } from "~/managers/use-register-manager";
import { useFunctionManager } from "~/managers/use-function-manager";
import { useOrderManager } from "~/managers/use-order-manager";
import { useRegisterStore } from "~/stores/use-register-store";
import LoadingSpinner from "../miscellaneous/loading-spinner";
import ReceiptComponent from "../order/receipt";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { RegisterButton } from "../ui/register-button";

const EmailReceipt = ({
  receipt,
  onClose,
}: {
  receipt: HTMLDivElement | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation("common");
  const [isSending, setIsSending] = useState(false);
  const [sendingProgress, setSendingProgress] = useState(0);
  // const { startUpload } = useUploadThing("pdfUploader", {
  //   headers: { Authorization: `Bearer ${session?.access_token}` },
  //   onUploadError: () => {
  //     alert("error occurred while uploading");
  //   },
  //   onUploadProgress(p) {
  //     setSendingProgress(p / 2);
  //   },
  //   onClientUploadComplete() {
  //     setSendingProgress(75);
  //   },
  // });

  const form = useForm({
    schema: z.object({
      email: z.string().email(),
    }),
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    setIsSending(true);
    const pdfBlob = await generatePdf();

    if (pdfBlob) {
      const file = new File(
        [pdfBlob],
        `receipt-${generateRandomString(10)}.pdf`,
      );

      // const result = await startUpload([file]);
      // const fileUrl = result?.at(0)?.url;

      // if (!fileUrl) return;

      // sendReceiptByEmail({
      //   email: data.email,
      //   pdfUrl: fileUrl,
      // });
    }
  });

  const generatePdf = async () => {
    if (!receipt) return;
    const canvas = await html2canvas(receipt, {
      scale: 3,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight, undefined, "FAST");
    const pdfBlob = pdf.output("blob");
    return pdfBlob;
  };

  // useEffect(() => {
  //   if (sendingEmail === false) {
  //     setSendingProgress(100);
  //   }
  // }, [sendingEmail]);

  useEffect(() => {
    if (sendingProgress === 100) {
      setTimeout(() => {
        setIsSending(false);
        setSendingProgress(0);
      }, 500);
    }
  }, [sendingProgress]);

  return (
    <Form {...form}>
      {isSending && (
        <div className="flex flex-col gap-2 items-center">
          <LoadingSpinner size={20} />
          <Progress value={sendingProgress} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("email_address")}</FormLabel>
              <FormControl>
                <Input disabled={isSending} {...field} type="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <RegisterButton type="submit" variant="success" disabled={isSending}>
          {t("send")}
        </RegisterButton>
      </form>
    </Form>
  );
};

export default function ReceiptScreen({
  receipt,
  onClose,
  showUnfinalizeButton = true,
}: {
  receipt: Receipt;
  onClose: () => void;
  showUnfinalizeButton?: boolean;
}) {
  const { t } = useTranslation("common");
  const receiptRef = useRef<HTMLDivElement>(null);
  const [showEmailReceiptOpen, setShowEmailReceiptOpen] = useState(false);
  const { createTicket, unFinalizeOrder } = useOrderManager();
  const { getFunction } = useFunctionManager();
  const { setDayOverviewOpen } = useRegisterStore();

  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const handleUnfinalizeOrder = async () => {
    if (!receipt.orderId) return;
    setIsLoading(true);
    const result = await unFinalizeOrder(receipt.orderId);
    setIsLoading(false);
    setDayOverviewOpen(false);

    if (result !== null) {
      onClose();
      void navigate({
        to: "/cash-register",
        search: {
          orderId: result.id,
          tableId: result.tableId,
        },
      });
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-2 overflow-hidden">
      <div className="overflow-auto hide-scrollbar max-h-[60vh] flex-1 gap-2">
        {receipt.orderLines.find(
          (orderLine) => orderLine.type === OrderLineTypes.comment,
        ) && (
          <p className="text-center">
            Comments will not be shown on physical receipt.
          </p>
        )}
        <ReceiptComponent ref={receiptRef} receipt={receipt} />
      </div>
      <div className="flex flex-col justify-between md:w-1/3 gap-2 overflow-auto">
        <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
          <RegisterButton
            className="text-xl col-span-full"
            variant="success"
            onClick={async () => {
              await createTicket({
                ticketItems: receipt.orderLines,
                receiptNumber: receipt.number,
                receiptDate: receipt.createdAt,
                tableReference: receipt.table,
                employeeName: receipt.employee,
                payments: receipt.payments,
              });
            }}
          >
            <p className="line-clamp-2 w-full truncate text-ellipsis whitespace-normal break-words">
              {t("Checkout.print_receipt")}
            </p>
          </RegisterButton>

          <Dialog
            open={showEmailReceiptOpen}
            onOpenChange={setShowEmailReceiptOpen}
          >
            <DialogTrigger asChild>
              <RegisterButton className="text-xl" disabled={true}>
                <p className="line-clamp-2 w-full truncate text-ellipsis whitespace-normal break-words">
                  {t("Checkout.email_receipt")}
                </p>
              </RegisterButton>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("Checkout.email_receipt")}</DialogTitle>
                <DialogDescription>
                  Enter the e-mailadress of the recipient
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
                <EmailReceipt
                  onClose={() => {
                    setShowEmailReceiptOpen(false);
                  }}
                  receipt={receiptRef.current}
                />
              </div>
            </DialogContent>
          </Dialog>
          <RegisterButton
            className="text-xl"
            onClick={() => {
              void getFunction(RegisterFunctions.open_drawer)?.();
            }}
          >
            <p className="line-clamp-2 w-full truncate text-ellipsis whitespace-normal break-words">
              {t("open_drawer")}
            </p>
          </RegisterButton>
        </div>
        {showUnfinalizeButton && (
          <Dialog>
            <DialogTrigger asChild>
              <RegisterButton className="text-xl" variant="destructive">
                <p className="line-clamp-2 w-full truncate text-ellipsis whitespace-normal break-words">
                  {t("Order.unfinalize")}
                </p>
              </RegisterButton>
            </DialogTrigger>
            <DialogContent className="max-w-lg w-full">
              <DialogHeader>
                <DialogTitle>{t("Order.unfinalize")}</DialogTitle>
                <VisuallyHidden>
                  <DialogDescription></DialogDescription>
                </VisuallyHidden>
              </DialogHeader>
              <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
                <div className="text-center text-lg">
                  {t("Order.before_unfinalize_message")}
                </div>
              </div>
              <DialogFooter>
                <div className="flex flex-row justify-center gap-10 w-full">
                  <DialogClose asChild>
                    <Button
                      disabled={isLoading}
                      className="text-xl h-full w-[150px]"
                    >
                      {t("cancel")}
                    </Button>
                  </DialogClose>

                  <Button
                    disabled={isLoading}
                    className="text-xl w-[150px] h-full"
                    onClick={handleUnfinalizeOrder}
                  >
                    {t("ok")}
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
